import React from "react";
import { DataTableOption } from "../../DataTable/DataTypes";
import { View } from "./interface";
import { GenericDassQuery } from "../../../../services/BasicDassQueries";

interface ViewAndColumnFields {
  VIEW_FIELDS_DEFAULT: Record<View, Set<string>>;
  COLUMN_FIELDS_DEFAULT: Map<string, string | React.ReactElement>;
}

export function generateColumnAndViewFields(dataTableOption: DataTableOption): ViewAndColumnFields {
  const basicViewSet = new Set<string>();
  const columnMap = new Map<string, string | React.ReactElement>();
  const columns = dataTableOption?.columns || [];
  for (const col of columns) {
    const field =  col?.key;
    if (field) {
        if(typeof col.title === "string") {
            if (!(field === "bulk_action_checkbox" || field === "action_button")) {
                columnMap.set(field, col.title);
            }
        }
        else if(col.title && React.isValidElement(col.title)) {
                columnMap.set(field, col.title);
        }
    }
    if(col.enabledInColumn !== false) {
        basicViewSet.add(field);
    }
  }
  return {
    VIEW_FIELDS_DEFAULT: {
      [View.Basic]: basicViewSet,
      [View.Detailed]: new Set(),  
      [View.Expanded]: new Set(),  
    },
    COLUMN_FIELDS_DEFAULT: columnMap,
  };
}

export const adjustColumnWidths = (columns, adjustableKeys = [], targetWidth = 1470) => {

  if (adjustableKeys.length === 0) {
    return columns;
  }

  const visibleCols = columns.filter(col => !col.hide);

  const adjustableCols = visibleCols.filter( col => adjustableKeys.includes(col.field) && typeof col.minWidth === 'number');

  const currentTotalWidth = visibleCols.reduce((sum, col) => sum + (col.minWidth || 0), 0);

  const totalAdjustmentNeeded = targetWidth - currentTotalWidth;

  if (totalAdjustmentNeeded === 0 || adjustableCols.length === 0) {
    return columns; 
  }

  const perColAdjustment = Math.floor(totalAdjustmentNeeded / adjustableCols.length);
  let remainder = totalAdjustmentNeeded - (perColAdjustment * adjustableCols.length);

  const updatedColumns = columns.map((col) => {
    if (!col.hide && adjustableKeys.includes(col.field)) {
      const extra = remainder > 0 ? 1 : (remainder < 0 ? -1 : 0);
      remainder -= extra;
      const newWidth = (col.minWidth || 0) + perColAdjustment + extra;

      return {
        ...col,
        width: newWidth,
        minWidth: newWidth,
        maxWidth: undefined
      };
    }

    return col;
  });

  return updatedColumns;
};
/* 
export const getDefaultFilterQueryObj = (dataTableOption: DataTableOption): Record<string, string> => {
 const defaultFilterQueryObj: Record<string, string> = {};

dataTableOption?.columns?.forEach((col) => {
  if (
    col.filterField &&
    Array.isArray(col.defaultFilterValue) &&
    col.defaultFilterValue.length > 0
  ) {
    defaultFilterQueryObj[col.filterField] = col.defaultFilterValue[0]; // take only index 0
  }
});

  return defaultFilterQueryObj;
};
 */
export const fetchPageState = async (fetchUrl: string,fetchUrlParamsRef: URLSearchParams, queryParamsString: string, limit: number) : Promise<{ pageStates: string[]; total: number }> => {
    let pageStateArray: string[] = [];
    try{
      fetchUrlParamsRef.delete("page_state");           
      fetchUrlParamsRef.set("get_pages", String(true));
      fetchUrlParamsRef.set("limit", limit.toString());
      const fetchUrlParamsString = "?" + ((fetchUrlParamsRef.size > 0) ? fetchUrlParamsRef.toString() : "");
      const FETCH_PAGE_STATE_URL = `${fetchUrl}${fetchUrlParamsString}${queryParamsString}`;

      const response = await GenericDassQuery(FETCH_PAGE_STATE_URL, { method: "GET" });
      fetchUrlParamsRef.delete("get_pages");
      fetchUrlParamsRef.delete("limit");
      if (response.status) {
        const data = await response.data;
        if (typeof data === "object" && data !== null) {
          if (data.total === 0) {
            return {pageStates: [], total: 0};
          }
          else {
            for(const page of data.pages) {
              if (page.page_state) {
                pageStateArray.push(page.page_state);
              }
            }
            return {pageStates: pageStateArray, total: data.total};
          }
        }
      } else {
        console.error("Error fetching data:", response.status);
        return {pageStates: [], total: 0};
      }
    }
    catch (error) {
      if (error?.status === 404) {
        console.warn("Invalid page_state, resetting and retrying...");
      } else {
        console.warn(error);
      }
    }
    return {pageStates: [], total: 0};
  };

export const fetchPageRows = async (fetchUrl: string, fetchUrlParamsRef: URLSearchParams, queryParamsString: string, pageState: string, limit: number): Promise<any[]> => {
  fetchUrlParamsRef.delete("get_pages");
  fetchUrlParamsRef.set("limit", limit.toString());
  fetchUrlParamsRef.set("page_state", pageState);
  const fetchUrlParamsString = "?" + ((fetchUrlParamsRef.size > 0) ? fetchUrlParamsRef.toString() : "");
  const rows = await fetchRowData(fetchUrl, queryParamsString, fetchUrlParamsString);
  fetchUrlParamsRef.delete("page_state");
  fetchUrlParamsRef.delete("limit");
  return rows;
};

export const fetchRowData = async (fetchUrl: string, queryParamsString: string, fetchUrlParamsString: string) : Promise<any[]> => {
    try {
      const REQ_URL = `${fetchUrl}${fetchUrlParamsString}${queryParamsString}`;
      const response = await GenericDassQuery(REQ_URL, { method: "GET" });
      if (response.status === 200) {
          const data = await response.data;
          return Array.isArray(data) ? data : [];
      }
      return [];
    } catch (error) {
      console.error(`Error fetching row data :`, error);
      return [];
    }
};

export const filterSelectedRowData = ( rows: any[], deSelectedRowData: Map<string, any>, primaryKey: string): any[] => {
  return rows.filter(row => {
    const rowId = row[primaryKey];
    return !deSelectedRowData.has(rowId); 
  });
};

export const getDefaultFilterParams = (dataTableOption: DataTableOption,  urlSearch: string = ""): URLSearchParams => {
 
  const columns = dataTableOption.columns || [];
  if (columns.length === 0) {
    return new URLSearchParams();
  }
  const defaultFilterQueryParams = new URLSearchParams();
  for (const col of columns) {
    const field =  col?.key;
    if(col?.filterField === "date"){
      if(col?.filterParams?.startField && col.defaultFilterValue?.[0]?.startDate){
        defaultFilterQueryParams.set(col?.filterParams?.startField, col.defaultFilterValue[0].startDate);
      }
      if(col?.filterParams?.endField && col.defaultFilterValue?.[0]?.endDate){
        defaultFilterQueryParams.set(col?.filterParams?.endField, col.defaultFilterValue[0].endDate);
      }
    }
    else{
      if (field && col.filterField && Array.isArray(col.defaultFilterValue) && col.defaultFilterValue.length > 0) {
        const filterValue = col.defaultFilterValue[0];
        if (filterValue) {
          // If a mapper is provided, apply it (support sync or Promise-returning mappers)
          try {
            const maybeMapped = col?.filterParams?.mapper ? col.filterParams.mapper(filterValue) : filterValue;
            if (maybeMapped && typeof (maybeMapped as any).then === 'function') {
              const mapped =  (maybeMapped as Promise<any>);
              // if mapper returns undefined/null, fall back to original filterValue
              defaultFilterQueryParams.set(col.filterField, mapped ?? filterValue);
            } else {
              defaultFilterQueryParams.set(col.filterField, maybeMapped ?? filterValue);
            }
          } catch (err) {
            // on mapper error, fall back to raw value
            defaultFilterQueryParams.set(col.filterField, filterValue);
          }
        }
      }
    }
  }
  // override existing URL params if have query params search
  if (urlSearch) {
    const urlParams = new URLSearchParams(urlSearch);
    urlParams.forEach((value, key) => {
      defaultFilterQueryParams.set(key, value);
    });
  }
  return defaultFilterQueryParams;
    
};

// --- Adaptive Throttler ---
export class AdaptiveThrottler {
    private last3: number[] = [];

    recordBatchTime(avgMs: number) {
        if (this.last3.length >= 3) {
            this.last3.shift();
        }
        this.last3.push(avgMs);
    }

    shouldPause(): boolean {
        if (this.last3.length < 2) return false;

        const currAvg = this.last3[this.last3.length - 1];
        let weightedPrevAvg: number;

        if (this.last3.length === 2) {
            // Only one previous batch → use it directly
            weightedPrevAvg = this.last3[0];
        } else {
            /*
                so instead of simple - 
                weightedPrevAvg = (prev1 +prev2)/2
                Prev1 (high weight) & Prev2 (lower weight)
                
                weighted average - (Normalized version)
                weightedPrevAvg = (prev1 * 0.7) + (prev2 * 0.3);                       
            */
            const prev1 = this.last3[this.last3.length - 2];
            const prev2 = this.last3[this.last3.length - 3];
            weightedPrevAvg = (prev1 * 0.7) + (prev2 * 0.3);
        }   
        return currAvg > weightedPrevAvg * 1.5;
    }
}